import { Box, HStack, Tooltip, VStack, Text } from '@chakra-ui/react';
import { djs } from '@duke-hq/djs';
import { CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { CopyToClipboard } from '@duke-hq/ui/components/CopyToClipboard/CopyToClipboard';

export type ValueTypes = string | null | undefined | Date;

const displayValue = (value: ValueTypes) => {
  if (value instanceof Date) {
    const date = djs(value);

    if (date.isValid() === false) {
      return '-';
    }
    return date.format('DD-MM-YYYY');
  }

  return value || '-';
};

/**
 * reusable component to display a single field, usually inside a grid
 */
export function SingleField({
  name,
  value,
  copyToClipboard = false,
  warning,
  success,
  info,
  ...rest
}: {
  name: React.ReactNode;
  value: ValueTypes | ValueTypes[];
  copyToClipboard?: boolean;
  warning?: string | null;
  success?: string | null;
  info?: string | null;
}) {
  return (
    <VStack alignItems="flex-start" gap={0} {...rest}>
      <Text textStyle="sm" color="fg.muted" mb={0} mt={2} fontSize="xs">
        {name}
      </Text>
      {Array.isArray(value) ? (
        value.map((v, index) => (
          <Box key={`${index}-${v?.toString()}`}>
            <Text mt={0}>{v?.toString()}</Text>
          </Box>
        ))
      ) : (
        <HStack spacing="2">
          <CopyToClipboard value={copyToClipboard && value != null ? value.toString() : undefined}>
            <Text mt={0}>{displayValue(value)}</Text>
          </CopyToClipboard>
          {warning != null && warning !== '' ? (
            <Tooltip label={warning} hasArrow placement="top">
              <Box as="span" display="inline-flex" color="orange.500" cursor="help">
                <TriangleAlert size={iconSize.sm} aria-label={warning} />
              </Box>
            </Tooltip>
          ) : null}
          {success != null && success !== '' ? (
            <Tooltip label={success} hasArrow placement="top">
              <Box as="span" display="inline-flex" color="green.500" cursor="help">
                <CircleCheck size={iconSize.sm} aria-label={success} />
              </Box>
            </Tooltip>
          ) : null}
          {info != null && info !== '' ? (
            <Tooltip label={info} hasArrow placement="top">
              <Box as="span" display="inline-flex" color="fg.muted" cursor="help">
                <Info size={iconSize.sm} aria-label={info} />
              </Box>
            </Tooltip>
          ) : null}
        </HStack>
      )}
    </VStack>
  );
}
